import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { LightColors, Tooltip, Typography } from "@thingsw/pitta-design-system";
import _ from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { API_GATEWAY_URI } from "../contants/Server";

import { Webviewer } from "../contants/Breakpoints";
import { IDrow } from "../types";
import clsx from "clsx";
import axios from "axios";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: "relative",
    boxShadow: "none",
    margin: "0 16px",
    fontFamily: "Roboto",
    [theme.breakpoints.down(Webviewer.mobile)]: {
      paddingTop: theme.spacing(2),
    },
  },
  timeSelectorDiv: {
    width: "calc(50% - 20px)",
    height: 210,
    position: "absolute",
    cursor: "pointer",
    top: 0,
    left: "calc(50% + 20px)",
    zIndex: 9,
    [theme.breakpoints.down(Webviewer.mobile)]: {
      width: "432px",
      left: "474px",
    },
  },
  eyeStatus: {
    display: "flex",
    flexWrap: "wrap",
    color: "#BEBEC1",
  },
  graphKey: {
    flex: 1,
    fontWeight: "bold",
    top: 0,
    right: "20px",
    marginLeft: "auto",
    textAlign: "right",
    [theme.breakpoints.down(Webviewer.mobile)]: {
      right: 0,
      fontSize: "0.9em",
    },
    "& p": {
      margin: "0 8px",
      display: "inline-block",
      [theme.breakpoints.down(Webviewer.mobile)]: {
        margin: "0 0 0 20px",
      },
    },
    "& .dfDrowsy": {
      color: "#FA4D56",
      "&:before": {
        content: "''",
        transform: "translate(-6px, 0)",
        height: "10px",
        width: "8px",
        backgroundColor: "#FA4D56",
        display: "inline-block",
      },
      cursor: "default",
    },
    "& .dfLeye": {
      color: "#33B1FF",
      "&:before": {
        content: "''",
        transform: "translate(-6px, -4px)",
        height: "2px",
        width: "12px",
        backgroundColor: "#33B1FF",
        display: "inline-block",
      },
      cursor: "default",
    },
    "& .dfReye": {
      color: "#00AA64",
      marginRight: "0",
      "&:before": {
        content: "''",
        transform: "translate(-6px, -4px)",
        height: "2px",
        width: "12px",
        backgroundColor: "#00AA64",
        display: "inline-block",
      },
      cursor: "default",
    },
  },
  graphContainer: {
    position: "relative",
    display: "block",
    fontSize: "0.83rem",
    [theme.breakpoints.down(Webviewer.mobile)]: {
      overflow: "auto",
      paddingTop: 18,
      paddingRight: 10,
    },
    "& > svg": {
      minWidth: "100%",
      display: "block",
      overflow: "visible",
      [theme.breakpoints.up(Webviewer.mobile)]: {
        maxWidth: "100%",
      },
    },
  },
  mobileSwipeNotice: {
    display: "block",
    zIndex: 9,
    opacity: 0,
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "288px",
    padding: "8px 16px",
    color: "#ffffff",
    textAlign: "center",
    transform: "translate(-50%, -50%)",
    transition: "opacity 1s",
    backgroundColor: "rgba(19, 19, 28, 0.7)",
    borderRadius: "3px",
    [theme.breakpoints.down(Webviewer.mobile)]: {
      "&.opened": {
        opacity: 1,
      },
    },
  },
  loadingDiv: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(19, 19, 28, 0.45)",
  },
  circularLoading: {
    color: LightColors.primary["0"],
  },
}));

interface DrowsyGraphProps {
  drows: IDrow[];
  drowsTime: string | undefined;
  timestamp?: number;
  loading?: boolean;
  onChangeTime: (timestamp: number) => void;
}

const defaultWidth = 596;
const startChartX = 40; //X coordinate of Chart area start

export const DrowsyGraph = ({
  drows,
  drowsTime,
  timestamp,
  loading,
  onChangeTime,
}: DrowsyGraphProps) => {
  const classes = useStyles();
  const theme = useTheme();  
  const containerDivRef = useRef<HTMLDivElement>(null);
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const mobileSwipeNotice = useRef<HTMLDivElement>(null);
  const selectDivRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [width, setWidth] = useState(defaultWidth);
  const [openDrowsyTooltip, setOpenDrowsyTooltip] = useState(false);
  const [openLeftEyeTooltip, setOpenLeftEyeTooltip] = useState(false);
  const [openRightEyeTooltip, setOpenRightEyeTooltip] = useState(false);
  const [openSwipeNotice, setOpenSwipeNotice] = useState(false);
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));

  const [time, setTime] = useState<moment.Moment>();
  const [totalT, setTotalT] = useState(0);
  const [drowsTotal, setDrowsTotal] = useState(0);
  const [firstDrowTimestamp, setFirstDrowTimestamp] = useState<number>();
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    if (containerDivRef.current && !mobile) {      
      const element = containerDivRef.current;
      //@ts-ignore
      const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0] && entries[0].contentRect.width > defaultWidth) {
          setWidth(entries[0].contentRect.width);
          setChartWidth(entries[0].contentRect.width - 74);
        } else {
          setWidth(defaultWidth);
          setChartWidth(defaultWidth - 74);
        }
      });
      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    } else {
      setWidth(941);
      setChartWidth(867);
    }
  }, [mobile]);

  useEffect(() => {
    const ftimestamp = _.first(drows)?.timestamp ?? 0;
    const errCnt = drows.filter(
      (drow) => drow.timestamp - ftimestamp < 0
    ).length;
    setFirstDrowTimestamp(ftimestamp);
    setDrowsTotal(drows.length - errCnt);
    setTime(moment.unix(Number(drowsTime)));
  }, [drows, drowsTime]);

  useEffect(() => {
    if (mobile) {
      setOpenSwipeNotice(true);
      graphContainerRef.current?.scroll({ left: chartWidth / 2 + 34 });
      if (loading) {
        graphContainerRef.current?.addEventListener("touchstart", () => {
          setOpenSwipeNotice(false);
        });
        graphContainerRef.current?.addEventListener("scroll", () => {
          setOpenSwipeNotice(false);
        });
        graphContainerRef.current?.addEventListener("click", () => {
          setOpenSwipeNotice(false);
        });
      }
    } else {
      setOpenSwipeNotice(false);
    }
  }, [mobile, chartWidth, loading]);

  const t1Length = useMemo(() => {
    const vodStartTimestamp = Number(drowsTime);
    return drows.filter((drow) => drow.timestamp <= vodStartTimestamp).length;
  }, [drows, drowsTime]);

  const t2Length = useMemo(() => {
    const vodStartTimestamp = Number(drowsTime);
    const lastDrowsTimestamp = _.last(drows)?.timestamp ?? 0;
    return drows.filter((drow) => drow.timestamp > vodStartTimestamp && drow.timestamp <= lastDrowsTimestamp).length;
  }, [drows, drowsTime]);

  const handleTimeSelector = useCallback(
    (e) => {
      var rect = e.target.getBoundingClientRect();
      var x = e.clientX - rect.left;
      if (selectDivRef.current) {
        const ratio = x / selectDivRef.current.clientWidth;
        onChangeTime(ratio * totalT);
      }
    },
    [onChangeTime, totalT]
  );

  const startGraphX = useMemo(() => {
    const vodStartTimestamp = Number(drowsTime);
    let timeDiff = 0;
    if (vodStartTimestamp && firstDrowTimestamp) {
      timeDiff = Math.round((totalT - (vodStartTimestamp - firstDrowTimestamp)) * 10) / 10
    };
    return timeDiff <= 0 ? 1 : (timeDiff / totalT) * (chartWidth / 2);
  }, [drowsTime, totalT, firstDrowTimestamp, chartWidth]);

  const eyeStatusMarkup = useMemo(() => {
    return (
      <>
        <Tooltip
          disableTouchListener={mobile}
          placement="top-start"
          open={openDrowsyTooltip}
          onOpen={() => setOpenDrowsyTooltip(true)}
          onClose={() => setOpenDrowsyTooltip(false)}
          PopperProps={{
            style: { maxWidth: "154px" },
            modifiers: {
              offset: {
                enabled: true,
                offset: "-10px, -12px",
              },
              flip: {
                enabled: false,
              },
            },
          }}
          title={
            <Typography
              category="Default"
              variant="Caption"
              htmlColor={LightColors.primary["0"]}
            >
              {t("Drowsy status triggers when the driver's eyes are closed for over 1 second.")}
            </Typography>
          }
        >
          <p className={"dfDrowsy"}>{t("Drowsy")}</p>
        </Tooltip>
        <Tooltip
          disableTouchListener={mobile}
          placement="top-start"
          open={openLeftEyeTooltip}
          onOpen={() => setOpenLeftEyeTooltip(true)}
          onClose={() => setOpenLeftEyeTooltip(false)}
          PopperProps={{
            style: { maxWidth: "154px" },
            modifiers: {
              offset: {
                enabled: true,
                offset: "-10px, -12px",
              },
              flip: {
                enabled: false,
              },
            },
          }}
          title={
            <Typography
              category="Default"
              variant="Caption"
              htmlColor={LightColors.primary["0"]}
            >
              {t("Driver's left eye openness level over time")}
            </Typography>
          }
        >
          <p className={"dfLeye"}>{t("Left eye")}</p>
        </Tooltip>
        <Tooltip
          disableTouchListener={mobile}
          placement="top-end"
          open={openRightEyeTooltip}
          onOpen={() => setOpenRightEyeTooltip(true)}
          onClose={() => setOpenRightEyeTooltip(false)}
          PopperProps={{
            style: { maxWidth: "154px" },
            modifiers: {
              offset: {
                enabled: true,
                offset: "5px, -12px",
              },
              flip: {
                enabled: false,
              },
            },
          }}
          title={
            <Typography
              category="Default"
              variant="Caption"
              htmlColor={LightColors.primary["0"]}
            >
              {t("Driver's right eye openness level over time")}
            </Typography>
          }
        >
          <p className={"dfReye"}>{t("Right eye")}</p>
        </Tooltip>
      </>
    );
  }, [mobile, openDrowsyTooltip, openLeftEyeTooltip, openRightEyeTooltip, t]);

  const drowsyGraphMarkup = useMemo(() => {
    if(t2Length > 0){
      const t2TotalT = t2Length / 4 - 1;
      setTotalT(t2TotalT);
    }
    if (t1Length > 0 && chartWidth > 0) {
      const drowsStartIdx = t1Length > totalT * 4 ? t1Length - totalT * 4 : 0;
      const drowsEndIdx = t2Length > 240 ? t1Length + 240 : t1Length + t2Length;
      const drowsRange = drowsEndIdx - drowsStartIdx;

      let drowDfNo = 0;
      let Df_cnt = 0;
      let drowsyCoordX = 0;
      let markUp = [];

      const getLinearData = (eye: string, color: string) => {
        return (
          _.chain(drows)
            .filter((drow, i) => i >= drowsStartIdx && i <= drowsEndIdx)
            .map((drow, i) => {
              let linearOne = [];
              let drowValue = (eye === "e_l") ? drow.e_l : drow.e_r;
              let drowErr = (drowValue >= 255 || drowValue === -1) ? true : false;
              let preDrow = (eye === "e_l") ? drows[i - 1]?.e_l : drows[i - 1]?.e_r;
              let nextDrow = (eye === "e_l") ? drows[i + 1]?.e_l : drows[i - 1]?.e_r;
              if (
                (drowErr === false && preDrow === undefined) ||
                (drowErr === false && nextDrow === undefined)
              ) {
                linearOne.push(
                  <stop
                    stopOpacity="1"
                    stopColor={color}
                    offset={i / drowsRange}
                  />
                );
              } else if (
                (drowErr === true && preDrow === undefined) ||
                (drowErr === true && nextDrow === undefined)
              ) {
                linearOne.push(
                  <stop
                    stopOpacity="0"
                    stopColor={color}
                    offset={i / drowsRange}
                  />
                );
              } else if (drowErr === true && (preDrow < 255 && preDrow !== -1)) {
                linearOne.push(
                  <>
                    <stop
                      stopOpacity="1"
                      stopColor={color}
                      offset={i / drowsRange}
                    />
                    <stop
                      stopOpacity="0"
                      stopColor={color}
                      offset={i / drowsRange}
                    />
                  </>
                );
              } else if (drowErr === false && (preDrow >= 255 || preDrow === -1)) {
                linearOne.push(
                  <>
                    <stop
                      stopOpacity="0"
                      stopColor={color}
                      offset={i / drowsRange}
                    />
                    <stop
                      stopOpacity="1"
                      stopColor={color}
                      offset={i / drowsRange}
                    />
                  </>
                );
              }
              return linearOne;
            })
            .value()
        );
      };

      const getPolylineData = (eye: string) => {
        let eyeErrY = 0;
        return (
          _.chain(drows)
            .filter((drow, i) => i >= drowsStartIdx && i <= drowsEndIdx)
            .map((drow, i) => {
              let drowValue = (eye === "e_l") ? drow.e_l : drow.e_r;
              if (drowValue > 60 && drowValue < 255) {
                eyeErrY = 60;
              }
              return `${(i / (drowsRange)) * (chartWidth - startGraphX) +
                startGraphX +
                startChartX
                },${drowValue <= 60 ? 192 - drowValue * 3.12 : 192 - eyeErrY * 3.12}`;
            })
            .join(" ")
            .value()
        );
      };

      markUp.push(
        <>
          <defs>
            <linearGradient
              id="poly2"
              x1="40"
              x2={chartWidth + 40}
              gradientUnits="userSpaceOnUse"
            >
              {getLinearData("e_r", "#00AA64")}
            </linearGradient>
          </defs>
          <defs>
            <linearGradient
              id="poly1"
              x1="40"
              x2={chartWidth + 40}
              gradientUnits="userSpaceOnUse"
            >
              {getLinearData("e_l", "#33B1FF")}
            </linearGradient>
          </defs>
          <polyline
            points={getPolylineData("e_r")}
            stroke="url(#poly2)"
            strokeWidth="1"
          />
          <polyline
            points={getPolylineData("e_l")}
            stroke="url(#poly1)"
            strokeWidth="1"
          />
          <rect
            x="40"
            y="4"
            width={chartWidth / 2}
            height="188"
            fill="#E9E9EA"
            fillOpacity="0.55"
          />
          <g transform={"translate(" + (chartWidth / 2 + 35) + " -14)"}>
            <text x="-70" y="10" fill="#7E7E83">
              {t("Video start")}
            </text>
            <path
              transform="translate(0 4)"
              d="M0.484375 0.984376L10.5156 0.984377L5.5 6L0.484375 0.984376Z"
              fill="#7E7E83"
            />
            <line
              x1="5"
              y1="19"
              x2="5"
              y2="209"
              stroke="#7E7E83"
              strokeDasharray="4 4"
              strokeWidth="1.5"
            />
          </g>
        </>
      );

      for (let i = t1Length + 16; i < drowsEndIdx; i++) {
        let dfValue = drows[i].d_f;
        if (drowDfNo > 0 && drowDfNo < 3 && dfValue !== drowDfNo) {
          markUp.push(
            <rect
              x={
                ((drowsyCoordX - drowsStartIdx) / drowsRange) *
                (chartWidth - startGraphX) +
                startChartX +
                startGraphX -
                16
              }
              y="4"
              width={Df_cnt + 16}
              height="188"
              fill="#FA4D56"
              fillOpacity="0.40"
            ></rect>
          );
          if (drowDfNo > 1) drowDfNo++;
          else if (drows[i].d_f < 2) drowDfNo = 0;
        }
        if (dfValue > drowDfNo) {
          drowsyCoordX = i;
          Df_cnt = 0;
          drowDfNo = dfValue;
        }
        Df_cnt++;
      }
      return markUp;
    }
  }, [
    drows,
    totalT,
    chartWidth,
    startGraphX,
    t1Length,
    t2Length,
    t,
  ]);

  useEffect(() => {
    const countDrowsyViews = async () => {  
      try {
        const resp = await axios.post(
          `${API_GATEWAY_URI}/BCS/countDrowsyGraphViews?type=success`);
        if (resp.data.resultcode === "BC_ERR_OK") {
          console.log("Drowsy View Counted!");
        }
      }catch (err) {
        console.log("errCode:", err);
      }
    };
    if(graphContainerRef.current){
      countDrowsyViews();
    }    
  }, []);

  return (
    <div className={classes.root} ref={containerDivRef}>
      <div className={classes.eyeStatus}>
        <div className={classes.graphKey}>{eyeStatusMarkup}</div>
      </div>
      {mobile && (
        <div
          className={clsx(
            classes.mobileSwipeNotice,
            openSwipeNotice ? "opened" : ""
          )}
          ref={mobileSwipeNotice}
        >
          {t("Swipe left and right to move")}
        </div>
      )}
      <div className={classes.graphContainer} ref={graphContainerRef}>
        <svg
          width={`${width - 32}`}
          height="100%"
          viewBox={`0 0 ${width - 32} 230`}
          fill="none"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ zIndex: 50 }}
        >
          <defs>
            <pattern
              id="grid"
              x={startChartX}
              y="2"
              width={chartWidth / 12}
              height="48"
              patternUnits="userSpaceOnUse"
            >
              <path
                d={"M " + chartWidth / 12 + " 0 L 0 0 0 50"}
                fill="none"
                stroke="#E9E9EA"
                strokeWidth="2"
              ></path>
            </pattern>
          </defs>
          <rect
            x={startChartX}
            y="4"
            width={chartWidth}
            height="188"
            fill="url(#grid)"
            stroke="#E9E9EA"
          ></rect>
          {time && totalT > 1 && (
            <>
              <g style={{ textAnchor: "middle" }}>
                <text x={startChartX + 8} y="214" fill="#BEBEC1">
                  {moment(time)
                    .subtract(_.round(totalT / 6) * 6, "s")
                    .format("mm:ss")}
                </text>
                <text x={chartWidth / 12 + startChartX} y="214" fill="#BEBEC1">
                  {moment(time)
                    .subtract(_.round(totalT / 6) * 5, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 2 + startChartX}
                  y="214"
                  fill="#BEBEC1"
                >
                  {moment(time)
                    .subtract(_.round(totalT / 6) * 4, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 3 + startChartX}
                  y="214"
                  fill="#BEBEC1"
                >
                  {moment(time)
                    .subtract(_.round(totalT / 6) * 3, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 4 + startChartX}
                  y="214"
                  fill="#BEBEC1"
                >
                  {moment(time)
                    .subtract(_.round(totalT / 6) * 2, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 5 + startChartX}
                  y="214"
                  fill="#BEBEC1"
                >
                  {moment(time)
                    .subtract(_.round(totalT / 6) * 1, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 6 + startChartX}
                  y="214"
                  fill="#68686E"
                >
                  {time.format("HH:mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 7 + startChartX}
                  y="214"
                  fill="#68686E"
                >
                  {moment(time)
                    .add(_.round(totalT / 6) * 1, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 8 + startChartX}
                  y="214"
                  fill="#68686E"
                >
                  {moment(time)
                    .add(_.round(totalT / 6) * 2, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 9 + startChartX}
                  y="214"
                  fill="#68686E"
                >
                  {moment(time)
                    .add(_.round(totalT / 6) * 3, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 10 + startChartX}
                  y="214"
                  fill="#68686E"
                >
                  {moment(time)
                    .add(_.round(totalT / 6) * 4, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 11 + startChartX}
                  y="214"
                  fill="#68686E"
                >
                  {moment(time)
                    .add(_.round(totalT / 6) * 5, "s")
                    .format("mm:ss")}
                </text>
                <text
                  x={(chartWidth / 12) * 12 + startChartX - 10}
                  y="214"
                  fill="#68686E"
                >
                  {moment(time)
                    .add(_.round(totalT / 6) * 6, "s")
                    .format("mm:ss")}
                </text>
              </g>
            </>
          )}
          <g>
            <g>
              <path
                d="M7.70703 26.9766V27.3359C7.70703 27.763 7.65365 28.1458 7.54688 28.4844C7.4401 28.8229 7.28646 29.1107 7.08594 29.3477C6.88542 29.5846 6.64453 29.7656 6.36328 29.8906C6.08464 30.0156 5.77214 30.0781 5.42578 30.0781C5.08984 30.0781 4.78125 30.0156 4.5 29.8906C4.22135 29.7656 3.97917 29.5846 3.77344 29.3477C3.57031 29.1107 3.41276 28.8229 3.30078 28.4844C3.1888 28.1458 3.13281 27.763 3.13281 27.3359V26.9766C3.13281 26.5495 3.1875 26.168 3.29688 25.832C3.40885 25.4935 3.56641 25.2057 3.76953 24.9688C3.97266 24.7292 4.21354 24.5469 4.49219 24.4219C4.77344 24.2969 5.08203 24.2344 5.41797 24.2344C5.76432 24.2344 6.07682 24.2969 6.35547 24.4219C6.63672 24.5469 6.8776 24.7292 7.07812 24.9688C7.28125 25.2057 7.4362 25.4935 7.54297 25.832C7.65234 26.168 7.70703 26.5495 7.70703 26.9766ZM6.96094 27.3359V26.9688C6.96094 26.6302 6.92578 26.3307 6.85547 26.0703C6.78776 25.8099 6.6875 25.5911 6.55469 25.4141C6.42188 25.237 6.25911 25.1029 6.06641 25.0117C5.8763 24.9206 5.66016 24.875 5.41797 24.875C5.18359 24.875 4.97135 24.9206 4.78125 25.0117C4.59375 25.1029 4.43229 25.237 4.29688 25.4141C4.16406 25.5911 4.0612 25.8099 3.98828 26.0703C3.91536 26.3307 3.87891 26.6302 3.87891 26.9688V27.3359C3.87891 27.6771 3.91536 27.9792 3.98828 28.2422C4.0612 28.5026 4.16536 28.7227 4.30078 28.9023C4.4388 29.0794 4.60156 29.2135 4.78906 29.3047C4.97917 29.3958 5.19141 29.4414 5.42578 29.4414C5.67057 29.4414 5.88802 29.3958 6.07812 29.3047C6.26823 29.2135 6.42839 29.0794 6.55859 28.9023C6.69141 28.7227 6.79167 28.5026 6.85938 28.2422C6.92708 27.9792 6.96094 27.6771 6.96094 27.3359ZM9.44922 26.5859V31.625H8.72266V25.7734H9.38672L9.44922 26.5859ZM12.2969 27.8516V27.9336C12.2969 28.2409 12.2604 28.526 12.1875 28.7891C12.1146 29.0495 12.0078 29.276 11.8672 29.4688C11.7292 29.6615 11.5586 29.8112 11.3555 29.918C11.1523 30.0247 10.9193 30.0781 10.6562 30.0781C10.388 30.0781 10.151 30.0339 9.94531 29.9453C9.73958 29.8568 9.5651 29.7279 9.42188 29.5586C9.27865 29.3893 9.16406 29.1862 9.07812 28.9492C8.99479 28.7122 8.9375 28.4453 8.90625 28.1484V27.7109C8.9375 27.3984 8.99609 27.1185 9.08203 26.8711C9.16797 26.6237 9.28125 26.4128 9.42188 26.2383C9.5651 26.0612 9.73828 25.9271 9.94141 25.8359C10.1445 25.7422 10.3789 25.6953 10.6445 25.6953C10.9102 25.6953 11.1458 25.7474 11.3516 25.8516C11.5573 25.9531 11.7305 26.099 11.8711 26.2891C12.0117 26.4792 12.1172 26.707 12.1875 26.9727C12.2604 27.2357 12.2969 27.5286 12.2969 27.8516ZM11.5703 27.9336V27.8516C11.5703 27.6406 11.5482 27.4427 11.5039 27.2578C11.4596 27.0703 11.3906 26.9062 11.2969 26.7656C11.2057 26.6224 11.0885 26.5104 10.9453 26.4297C10.8021 26.3464 10.6315 26.3047 10.4336 26.3047C10.2513 26.3047 10.0924 26.3359 9.95703 26.3984C9.82422 26.4609 9.71094 26.5456 9.61719 26.6523C9.52344 26.7565 9.44661 26.8763 9.38672 27.0117C9.32943 27.1445 9.28646 27.2826 9.25781 27.4258V28.4375C9.3099 28.6198 9.38281 28.7917 9.47656 28.9531C9.57031 29.112 9.69531 29.2409 9.85156 29.3398C10.0078 29.4362 10.2044 29.4844 10.4414 29.4844C10.6367 29.4844 10.8047 29.444 10.9453 29.3633C11.0885 29.2799 11.2057 29.1667 11.2969 29.0234C11.3906 28.8802 11.4596 28.7161 11.5039 28.5312C11.5482 28.3438 11.5703 28.1445 11.5703 27.9336ZM14.9727 30.0781C14.6784 30.0781 14.4115 30.0286 14.1719 29.9297C13.9349 29.8281 13.7305 29.6862 13.5586 29.5039C13.3893 29.3216 13.2591 29.1055 13.168 28.8555C13.0768 28.6055 13.0312 28.332 13.0312 28.0352V27.8711C13.0312 27.5273 13.082 27.2214 13.1836 26.9531C13.2852 26.6823 13.4232 26.4531 13.5977 26.2656C13.7721 26.0781 13.9701 25.9362 14.1914 25.8398C14.4128 25.7435 14.6419 25.6953 14.8789 25.6953C15.181 25.6953 15.4414 25.7474 15.6602 25.8516C15.8815 25.9557 16.0625 26.1016 16.2031 26.2891C16.3438 26.474 16.4479 26.6927 16.5156 26.9453C16.5833 27.1953 16.6172 27.4688 16.6172 27.7656V28.0898H13.4609V27.5H15.8945V27.4453C15.8841 27.2578 15.8451 27.0755 15.7773 26.8984C15.7122 26.7214 15.6081 26.5755 15.4648 26.4609C15.3216 26.3464 15.1263 26.2891 14.8789 26.2891C14.7148 26.2891 14.5638 26.3242 14.4258 26.3945C14.2878 26.4622 14.1693 26.5638 14.0703 26.6992C13.9714 26.8346 13.8945 27 13.8398 27.1953C13.7852 27.3906 13.7578 27.6159 13.7578 27.8711V28.0352C13.7578 28.2357 13.7852 28.4245 13.8398 28.6016C13.8971 28.776 13.9792 28.9297 14.0859 29.0625C14.1953 29.1953 14.3268 29.2995 14.4805 29.375C14.6367 29.4505 14.8138 29.4883 15.0117 29.4883C15.2669 29.4883 15.4831 29.4362 15.6602 29.332C15.8372 29.2279 15.9922 29.0885 16.125 28.9141L16.5625 29.2617C16.4714 29.3997 16.3555 29.5312 16.2148 29.6562C16.0742 29.7812 15.901 29.8828 15.6953 29.9609C15.4922 30.0391 15.2513 30.0781 14.9727 30.0781ZM18.1836 26.6758V30H17.4609V25.7734H18.1445L18.1836 26.6758ZM18.0117 27.7266L17.7109 27.7148C17.7135 27.4258 17.7565 27.1589 17.8398 26.9141C17.9232 26.6667 18.0404 26.4518 18.1914 26.2695C18.3424 26.0872 18.5221 25.9466 18.7305 25.8477C18.9414 25.7461 19.1745 25.6953 19.4297 25.6953C19.638 25.6953 19.8255 25.724 19.9922 25.7812C20.1589 25.8359 20.3008 25.9245 20.418 26.0469C20.5378 26.1693 20.6289 26.3281 20.6914 26.5234C20.7539 26.7161 20.7852 26.9518 20.7852 27.2305V30H20.0586V27.2227C20.0586 27.0013 20.026 26.8242 19.9609 26.6914C19.8958 26.556 19.8008 26.4583 19.6758 26.3984C19.5508 26.3359 19.3971 26.3047 19.2148 26.3047C19.0352 26.3047 18.8711 26.3424 18.7227 26.418C18.5768 26.4935 18.4505 26.5977 18.3438 26.7305C18.2396 26.8633 18.1576 27.0156 18.0977 27.1875C18.0404 27.3568 18.0117 27.5365 18.0117 27.7266Z"
                fill="#BEBEC1"
              />
              <path
                d="M5.6399 8.29999C7.56535 7.09751 9.78981 6.45996 12.0599 6.45996C14.33 6.45996 16.5544 7.09751 18.4799 8.29999C20.2527 9.4143 21.6471 11.0388 22.4799 12.96C21.6438 14.8765 20.25 16.4969 18.4799 17.61C16.5569 18.8192 14.3315 19.4608 12.0599 19.4608C9.7883 19.4608 7.56288 18.8192 5.6399 17.61C3.86982 16.4969 2.47596 14.8765 1.63989 12.96C2.47267 11.0388 3.86706 9.4143 5.6399 8.29999V8.29999Z"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
              <path
                d="M9.21833 10.0257C9.88133 9.38406 10.7458 8.99133 11.6652 8.91407C12.5846 8.83682 13.5025 9.0798 14.2632 9.60186C15.024 10.1239 15.5809 10.8929 15.8396 11.7786C16.0983 12.6643 16.0428 13.6122 15.6826 14.4616C15.3224 15.3111 14.6796 16.0099 13.8631 16.4397C13.0466 16.8694 12.1067 17.0037 11.2026 16.8197C10.2984 16.6357 9.48571 16.1448 8.90208 15.4302C8.31845 14.7156 7.99982 13.8212 8.00018 12.8985C7.99508 12.3617 8.10062 11.8295 8.3102 11.3353C8.51978 10.841 8.82891 10.3953 9.21833 10.0257V10.0257Z"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
            </g>
            <g>
              <path
                d="M5.6399 90.3C7.56535 89.0975 9.78981 88.46 12.0599 88.46C14.33 88.46 16.5544 89.0975 18.4799 90.3C20.2527 91.4143 21.6471 93.0388 22.4799 94.96C21.6438 96.8765 20.25 98.4969 18.4799 99.61C16.5569 100.819 14.3315 101.461 12.0599 101.461C9.7883 101.461 7.56288 100.819 5.6399 99.61C3.86982 98.4969 2.47596 96.8765 1.63989 94.96C2.47267 93.0388 3.86706 91.4143 5.6399 90.3V90.3Z"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
              <path
                d="M22.5 95H1.5"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
              <path
                d="M8.00024 94.8984C7.99989 95.8211 8.31852 96.7155 8.90214 97.4302C9.48577 98.1448 10.2985 98.6357 11.2026 98.8197C12.1068 99.0036 13.0467 98.8694 13.8632 98.4396C14.6797 98.0099 15.3225 97.311 15.6827 96.4616C15.8727 96.0133 15.978 95.5377 15.9969 95.0582"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
            </g>
            <g>
              <path
                d="M22 166C21.2057 168.1 19.9007 169.792 18.0851 171.075C16.2695 172.358 14.2411 173 12 173C9.75887 173 7.7305 172.358 5.91489 171.075C4.09929 169.792 2.79433 168.1 2 166"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
              <path
                d="M12.01 176.37V172.83"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
              <path
                d="M23.69 171.47L20.8101 169.01"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
              <path
                d="M17.1201 171.85L18.8701 175.03"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
              <path
                d="M5.1499 174.86L6.8999 171.69"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
              <path
                d="M3.20007 169L0.320068 171.47"
                stroke="#BEBEC1"
                strokeWidth="1.5"
              />
              <path
                d="M16 164.357V165H13.2108V164.357H16ZM15.9595 161.459L13.5703 165H13V164.527L15.4081 161H15.9595V161.459ZM15.6811 161V161.646H13.0027V161H15.6811Z"
                fill="#BEBEC1"
              />
              <path
                d="M22 162.196V163H18.2811V162.196H22ZM21.9459 158.573L18.7604 163H18V162.409L21.2108 158H21.9459V158.573ZM21.5748 158V158.807H18.0036V158H21.5748Z"
                fill="#BEBEC1"
              />
              <path
                d="M3.78906 186.191H4.53906C4.5 186.551 4.39714 186.872 4.23047 187.156C4.0638 187.44 3.82812 187.665 3.52344 187.832C3.21875 187.996 2.83854 188.078 2.38281 188.078C2.04948 188.078 1.74609 188.016 1.47266 187.891C1.20182 187.766 0.96875 187.589 0.773438 187.359C0.578125 187.128 0.427083 186.85 0.320312 186.527C0.216146 186.202 0.164062 185.84 0.164062 185.441V184.875C0.164062 184.477 0.216146 184.116 0.320312 183.793C0.427083 183.467 0.579427 183.189 0.777344 182.957C0.977865 182.725 1.21875 182.547 1.5 182.422C1.78125 182.297 2.09766 182.234 2.44922 182.234C2.87891 182.234 3.24219 182.315 3.53906 182.477C3.83594 182.638 4.06641 182.862 4.23047 183.148C4.39714 183.432 4.5 183.762 4.53906 184.137H3.78906C3.7526 183.871 3.6849 183.643 3.58594 183.453C3.48698 183.26 3.34635 183.112 3.16406 183.008C2.98177 182.904 2.74349 182.852 2.44922 182.852C2.19661 182.852 1.97396 182.9 1.78125 182.996C1.59115 183.092 1.43099 183.229 1.30078 183.406C1.17318 183.583 1.07682 183.796 1.01172 184.043C0.946615 184.29 0.914062 184.565 0.914062 184.867V185.441C0.914062 185.72 0.942708 185.982 1 186.227C1.0599 186.471 1.14974 186.686 1.26953 186.871C1.38932 187.056 1.54167 187.202 1.72656 187.309C1.91146 187.413 2.13021 187.465 2.38281 187.465C2.70312 187.465 2.95833 187.414 3.14844 187.312C3.33854 187.211 3.48177 187.065 3.57812 186.875C3.67708 186.685 3.7474 186.457 3.78906 186.191ZM6.24219 182V188H5.51562V182H6.24219ZM7.21094 185.934V185.844C7.21094 185.539 7.25521 185.257 7.34375 184.996C7.43229 184.733 7.5599 184.505 7.72656 184.312C7.89323 184.117 8.09505 183.966 8.33203 183.859C8.56901 183.75 8.83464 183.695 9.12891 183.695C9.42578 183.695 9.69271 183.75 9.92969 183.859C10.1693 183.966 10.3724 184.117 10.5391 184.312C10.7083 184.505 10.8372 184.733 10.9258 184.996C11.0143 185.257 11.0586 185.539 11.0586 185.844V185.934C11.0586 186.238 11.0143 186.521 10.9258 186.781C10.8372 187.042 10.7083 187.27 10.5391 187.465C10.3724 187.658 10.1706 187.809 9.93359 187.918C9.69922 188.025 9.43359 188.078 9.13672 188.078C8.83984 188.078 8.57292 188.025 8.33594 187.918C8.09896 187.809 7.89583 187.658 7.72656 187.465C7.5599 187.27 7.43229 187.042 7.34375 186.781C7.25521 186.521 7.21094 186.238 7.21094 185.934ZM7.93359 185.844V185.934C7.93359 186.145 7.95833 186.344 8.00781 186.531C8.05729 186.716 8.13151 186.88 8.23047 187.023C8.33203 187.167 8.45833 187.28 8.60938 187.363C8.76042 187.444 8.9362 187.484 9.13672 187.484C9.33464 187.484 9.50781 187.444 9.65625 187.363C9.80729 187.28 9.93229 187.167 10.0312 187.023C10.1302 186.88 10.2044 186.716 10.2539 186.531C10.306 186.344 10.332 186.145 10.332 185.934V185.844C10.332 185.635 10.306 185.439 10.2539 185.254C10.2044 185.066 10.1289 184.901 10.0273 184.758C9.92839 184.612 9.80339 184.497 9.65234 184.414C9.50391 184.331 9.32943 184.289 9.12891 184.289C8.93099 184.289 8.75651 184.331 8.60547 184.414C8.45703 184.497 8.33203 184.612 8.23047 184.758C8.13151 184.901 8.05729 185.066 8.00781 185.254C7.95833 185.439 7.93359 185.635 7.93359 185.844ZM14.4258 186.879C14.4258 186.775 14.4023 186.678 14.3555 186.59C14.3112 186.499 14.2188 186.417 14.0781 186.344C13.9401 186.268 13.7318 186.203 13.4531 186.148C13.2188 186.099 13.0065 186.04 12.8164 185.973C12.6289 185.905 12.4688 185.823 12.3359 185.727C12.2057 185.63 12.1055 185.517 12.0352 185.387C11.9648 185.257 11.9297 185.104 11.9297 184.93C11.9297 184.763 11.9661 184.605 12.0391 184.457C12.1146 184.309 12.2201 184.177 12.3555 184.062C12.4935 183.948 12.6589 183.858 12.8516 183.793C13.0443 183.728 13.2591 183.695 13.4961 183.695C13.8346 183.695 14.1237 183.755 14.3633 183.875C14.6029 183.995 14.7865 184.155 14.9141 184.355C15.0417 184.553 15.1055 184.773 15.1055 185.016H14.3828C14.3828 184.898 14.3477 184.785 14.2773 184.676C14.2096 184.564 14.1094 184.471 13.9766 184.398C13.8464 184.326 13.6862 184.289 13.4961 184.289C13.2956 184.289 13.1328 184.32 13.0078 184.383C12.8854 184.443 12.7956 184.52 12.7383 184.613C12.6836 184.707 12.6562 184.806 12.6562 184.91C12.6562 184.988 12.6693 185.059 12.6953 185.121C12.724 185.181 12.7734 185.237 12.8438 185.289C12.9141 185.339 13.013 185.385 13.1406 185.43C13.2682 185.474 13.431 185.518 13.6289 185.562C13.9753 185.641 14.2604 185.734 14.4844 185.844C14.7083 185.953 14.875 186.087 14.9844 186.246C15.0938 186.405 15.1484 186.598 15.1484 186.824C15.1484 187.009 15.1094 187.178 15.0312 187.332C14.9557 187.486 14.8451 187.618 14.6992 187.73C14.556 187.84 14.3841 187.926 14.1836 187.988C13.9857 188.048 13.763 188.078 13.5156 188.078C13.1432 188.078 12.8281 188.012 12.5703 187.879C12.3125 187.746 12.1172 187.574 11.9844 187.363C11.8516 187.152 11.7852 186.93 11.7852 186.695H12.5117C12.5221 186.893 12.5794 187.051 12.6836 187.168C12.7878 187.283 12.9154 187.365 13.0664 187.414C13.2174 187.461 13.3672 187.484 13.5156 187.484C13.7135 187.484 13.8789 187.458 14.0117 187.406C14.1471 187.354 14.25 187.283 14.3203 187.191C14.3906 187.1 14.4258 186.996 14.4258 186.879ZM17.8516 188.078C17.5573 188.078 17.2904 188.029 17.0508 187.93C16.8138 187.828 16.6094 187.686 16.4375 187.504C16.2682 187.322 16.138 187.105 16.0469 186.855C15.9557 186.605 15.9102 186.332 15.9102 186.035V185.871C15.9102 185.527 15.9609 185.221 16.0625 184.953C16.1641 184.682 16.3021 184.453 16.4766 184.266C16.651 184.078 16.849 183.936 17.0703 183.84C17.2917 183.743 17.5208 183.695 17.7578 183.695C18.0599 183.695 18.3203 183.747 18.5391 183.852C18.7604 183.956 18.9414 184.102 19.082 184.289C19.2227 184.474 19.3268 184.693 19.3945 184.945C19.4622 185.195 19.4961 185.469 19.4961 185.766V186.09H16.3398V185.5H18.7734V185.445C18.763 185.258 18.724 185.076 18.6562 184.898C18.5911 184.721 18.487 184.576 18.3438 184.461C18.2005 184.346 18.0052 184.289 17.7578 184.289C17.5938 184.289 17.4427 184.324 17.3047 184.395C17.1667 184.462 17.0482 184.564 16.9492 184.699C16.8503 184.835 16.7734 185 16.7188 185.195C16.6641 185.391 16.6367 185.616 16.6367 185.871V186.035C16.6367 186.236 16.6641 186.424 16.7188 186.602C16.776 186.776 16.8581 186.93 16.9648 187.062C17.0742 187.195 17.2057 187.299 17.3594 187.375C17.5156 187.451 17.6927 187.488 17.8906 187.488C18.1458 187.488 18.362 187.436 18.5391 187.332C18.7161 187.228 18.8711 187.089 19.0039 186.914L19.4414 187.262C19.3503 187.4 19.2344 187.531 19.0938 187.656C18.9531 187.781 18.7799 187.883 18.5742 187.961C18.3711 188.039 18.1302 188.078 17.8516 188.078ZM23.0039 187.18V182H23.7305V188H23.0664L23.0039 187.18ZM20.1602 185.934V185.852C20.1602 185.529 20.1992 185.236 20.2773 184.973C20.3581 184.707 20.4714 184.479 20.6172 184.289C20.7656 184.099 20.9414 183.953 21.1445 183.852C21.3503 183.747 21.5794 183.695 21.832 183.695C22.0977 183.695 22.3294 183.742 22.5273 183.836C22.7279 183.927 22.8971 184.061 23.0352 184.238C23.1758 184.413 23.2865 184.624 23.3672 184.871C23.4479 185.118 23.5039 185.398 23.5352 185.711V186.07C23.5065 186.38 23.4505 186.659 23.3672 186.906C23.2865 187.154 23.1758 187.365 23.0352 187.539C22.8971 187.714 22.7279 187.848 22.5273 187.941C22.3268 188.033 22.0924 188.078 21.8242 188.078C21.5768 188.078 21.3503 188.025 21.1445 187.918C20.9414 187.811 20.7656 187.661 20.6172 187.469C20.4714 187.276 20.3581 187.049 20.2773 186.789C20.1992 186.526 20.1602 186.241 20.1602 185.934ZM20.8867 185.852V185.934C20.8867 186.145 20.9076 186.342 20.9492 186.527C20.9935 186.712 21.0612 186.875 21.1523 187.016C21.2435 187.156 21.3594 187.267 21.5 187.348C21.6406 187.426 21.8086 187.465 22.0039 187.465C22.2435 187.465 22.4401 187.414 22.5938 187.312C22.75 187.211 22.875 187.077 22.9688 186.91C23.0625 186.743 23.1354 186.562 23.1875 186.367V185.426C23.1562 185.283 23.1107 185.145 23.0508 185.012C22.9935 184.876 22.918 184.757 22.8242 184.652C22.7331 184.546 22.6198 184.461 22.4844 184.398C22.3516 184.336 22.194 184.305 22.0117 184.305C21.8138 184.305 21.6432 184.346 21.5 184.43C21.3594 184.51 21.2435 184.622 21.1523 184.766C21.0612 184.906 20.9935 185.07 20.9492 185.258C20.9076 185.443 20.8867 185.641 20.8867 185.852Z"
                fill="#BEBEC1"
              />
            </g>
          </g>

          {(drowsTotal > 0 && totalT > 0) && (
            <>
              {drowsyGraphMarkup}

              <rect
                width={chartWidth / 2 / totalT}
                height="187"
                x={Math.max(
                  8,
                  ((Math.round(timestamp ?? 0) - 1) / totalT) *
                  (chartWidth / 2) +
                  (chartWidth / 2 + startChartX + 4)
                )}
                y="5"
                stroke="#D81A26"
                strokeWidth="2"
              />
            </>
          )}
        </svg>
        {loading && (
          <div className={classes.loadingDiv}>
            <CircularProgress
              className={classes.circularLoading}
              size={48}
              thickness={5}
            />
          </div>
        )}
        {!loading && drowsTotal > 0 && (
          <div
            ref={selectDivRef}
            className={classes.timeSelectorDiv}
            onClick={handleTimeSelector}
          />
        )}
      </div>
    </div>
  );
};